/**
 * Registre unique des types de champs — schéma payload, conditions, éditeur de valeur.
 * Les dates/heures utilisent string + format JSON Schema pour compatibilité backend.
 */

/** @typedef {{ value: string, label: string, format?: string, schemaType?: string }} FieldTypeOption */

/** @type {FieldTypeOption[]} */
export const PAYLOAD_FIELD_TYPE_OPTIONS = [
    { value: "string", label: "Texte", schemaType: "string" },
    { value: "integer", label: "Entier", schemaType: "integer" },
    { value: "number", label: "Nombre", schemaType: "number" },
    { value: "boolean", label: "Booléen", schemaType: "boolean" },
    { value: "date", label: "Date", schemaType: "string", format: "date" },
    { value: "datetime", label: "Date et heure", schemaType: "string", format: "date-time" },
    { value: "time", label: "Heure", schemaType: "string", format: "time" },
    { value: "email", label: "Email", schemaType: "string", format: "email" },
    { value: "phone", label: "Téléphone", schemaType: "string", format: "tel" },
    { value: "uri", label: "URL / URI", schemaType: "string", format: "uri" },
    { value: "uuid", label: "UUID", schemaType: "string", format: "uuid" },
    { value: "ipv4", label: "Adresse IPv4", schemaType: "string", format: "ipv4" },
    { value: "currency", label: "Montant", schemaType: "number" },
    { value: "percent", label: "Pourcentage", schemaType: "number" },
    { value: "array", label: "Tableau", schemaType: "array" },
    { value: "object", label: "Objet", schemaType: "object" },
];

const FORM_TYPE_VALUES = new Set(PAYLOAD_FIELD_TYPE_OPTIONS.map((o) => o.value));

const FORMAT_TO_FORM_TYPE = {
    date: "date",
    "date-time": "datetime",
    datetime: "datetime",
    time: "time",
    email: "email",
    "idn-email": "email",
    tel: "phone",
    phone: "phone",
    uri: "uri",
    url: "uri",
    "uri-reference": "uri",
    uuid: "uuid",
    ipv4: "ipv4",
    ipv6: "ipv4",
};

const FORMAT_TO_VALUE_KIND = {
    date: "date",
    "date-time": "datetime",
    datetime: "datetime",
    time: "time",
    email: "email",
    "idn-email": "email",
    tel: "phone",
    phone: "phone",
    uri: "uri",
    url: "uri",
    "uri-reference": "uri",
    uuid: "uuid",
    ipv4: "ipv4",
    ipv6: "ipv4",
};

const VALUE_KIND_WIDGET = {
    number: "number",
    date: "date",
    datetime: "datetime",
    time: "time",
    boolean: "boolean",
    enum: "select",
    email: "email",
    phone: "tel",
    uri: "url",
    uuid: "text",
    ipv4: "text",
    array: "array",
    object: "json",
};

const VALUE_KIND_HTML_INPUT = {
    number: "number",
    date: "date",
    datetime: "datetime-local",
    time: "time",
    email: "email",
    phone: "tel",
    uri: "url",
};

const FIELD_NAME_PATTERNS = [
    { pattern: /email/i, valueKind: "email" },
    { pattern: /(^|_|\.)(phone|telephone|tel|mobile|msisdn)$/i, valueKind: "phone" },
    { pattern: /(^|_|\.)(url|uri|link|website)$/i, valueKind: "uri" },
    { pattern: /(^|_|\.)(uuid|guid)$/i, valueKind: "uuid" },
    { pattern: /(^|_|\.)(ipv4|ip_address|ip)$/i, valueKind: "ipv4" },
    { pattern: /(^|_|\.)(orderdate|created_at|updated_at|_at)$/i, valueKind: "datetime" },
];

export function payloadFieldTypeLabel(type, format) {
    const formType = schemaNodeToFormType({ type, format });
    return PAYLOAD_FIELD_TYPE_OPTIONS.find((o) => o.value === formType)?.label || type || "Texte";
}

export function valueKindLabel(valueKind) {
    const fromOption = PAYLOAD_FIELD_TYPE_OPTIONS.find((o) => o.value === valueKind);
    if (fromOption) return fromOption.label;
    const map = {
        string: "Texte",
        enum: "Liste",
        set: "Ensemble",
        array: "Tableau",
        object: "Objet",
        birthdate: "Date de naissance",
        loyalty_tier: "Palier fidélité",
        reward: "Récompense",
    };
    return map[valueKind] || valueKind || "Texte";
}

/** UI type ← nœud schéma JSON (type + format). */
export function schemaNodeToFormType(node = {}) {
    if (Array.isArray(node?.enum) && node.enum.length > 0) return "string";

    const rawType = String(node?.type || "string").toLowerCase();
    const fmt = String(node?.format || "").toLowerCase();

    if (rawType === "string" && fmt) {
        const mapped = FORMAT_TO_FORM_TYPE[fmt];
        if (mapped) return mapped;
    }

    if (rawType === "int") return "integer";
    if (["float", "decimal", "double"].includes(rawType)) return "number";
    if (rawType === "date-time") return "datetime";
    if (FORM_TYPE_VALUES.has(rawType)) return rawType;
    return "string";
}

/** Nœud schéma ← type UI formulaire. */
export function formTypeToSchemaNode(formType, description) {
    const opt = PAYLOAD_FIELD_TYPE_OPTIONS.find((o) => o.value === formType);
    const node = {};
    if (opt) {
        node.type = opt.schemaType || opt.value;
        if (opt.format) node.format = opt.format;
    } else {
        node.type = "string";
    }
    if (description?.trim()) node.description = description.trim();
    return node;
}

/** valueKind pour moteur de conditions ← nœud schéma. */
export function schemaNodeToValueKind(node = {}) {
    let t = node.type;
    if (Array.isArray(t)) t = t.find((x) => x !== "null") || t[0];
    t = String(t || "string").toLowerCase();

    if (Array.isArray(node.enum) && node.enum.length > 0) return "enum";

    const fmt = String(node.format || "").toLowerCase();
    if (fmt && FORMAT_TO_VALUE_KIND[fmt]) return FORMAT_TO_VALUE_KIND[fmt];

    if (["integer", "int", "float", "number", "decimal", "double"].includes(t)) return "number";
    if (["bool", "boolean"].includes(t)) return "boolean";
    if (t === "array") return "array";
    if (t === "object") return "object";
    if (t === "date") return "date";
    if (["datetime", "date-time"].includes(t)) return "datetime";
    if (t === "time") return "time";
    if (t === "enum") return "enum";
    if (FORM_TYPE_VALUES.has(t) && !["string", "array", "object"].includes(t)) return t;
    return "string";
}

export function widgetForValueKind(valueKind) {
    return VALUE_KIND_WIDGET[valueKind] || "text";
}

export function htmlInputTypeForValueKind(valueKind, ui = {}) {
    if (ui.widget === "number") return "number";
    if (ui.widget === "date") return "date";
    if (ui.widget === "datetime") return "datetime-local";
    if (ui.widget === "email") return "email";
    if (ui.widget === "tel") return "tel";
    if (ui.widget === "url") return "url";
    if (ui.widget === "time") return "time";
    return VALUE_KIND_HTML_INPUT[valueKind] || "text";
}

export function inferValueKindFromFieldName(field) {
    if (!field || typeof field !== "string") return null;
    const short = field.includes(".") ? field.split(".").pop() : field;
    for (const { pattern, valueKind } of FIELD_NAME_PATTERNS) {
        if (pattern.test(field) || pattern.test(short)) return valueKind;
    }
    return null;
}

export function fieldMetaFromSchemaNode(node = {}) {
    const valueKind = schemaNodeToValueKind(node);
    const ui = { widget: widgetForValueKind(valueKind) };
    if (valueKind === "enum" && Array.isArray(node.enum)) {
        ui.options = [...node.enum];
    }
    if (valueKind === "array") {
        ui.itemsKind = node.items ? schemaNodeToValueKind(node.items) : "string";
    }
    const meta = { valueKind, ui };
    if (node.description) meta.label = String(node.description);
    if (node.format) meta.format = node.format;
    return meta;
}
